/**
 * RuntimeBridge — host-side thin client for cogo.runtime.* commands.
 *
 * Sends MCP commands to the Preview iframe via postMessage and awaits
 * JSON-RPC-style responses using correlation IDs. Implements the same
 * execute(cmd) -> Promise<McpResult> interface as StudioMcpBus so
 * callers use a uniform API.
 */

import { type McpCommand, type McpResult, RUNTIME_NS } from './types'
import {
  IFRAME_COMMANDS,
  IFRAME_EVENTS,
  createIframeCommand,
  deriveIframeOrigin,
} from '@cogo/iframe-protocol'

const TIMEOUT_MS = 10_000

class RuntimeBridge {
  private iframeRef: HTMLIFrameElement | null = null
  /** Origin the exchange is pinned to (derived from the iframe src). '*' =
   * underivable (about:blank/srcdoc) — source validation still applies. */
  private pinnedOrigin = '*'
  private pending = new Map<
    string,
    { resolve: (r: McpResult) => void; timer: ReturnType<typeof setTimeout> }
  >()
  private messageHandler: ((event: MessageEvent) => void) | null = null

  /** Connect to an iframe element. Call once when iframe loads. */
  connect(iframe: HTMLIFrameElement): void {
    this.disconnect()
    this.iframeRef = iframe
    this.pinnedOrigin = deriveIframeOrigin(iframe) ?? '*'

    this.messageHandler = (event: MessageEvent) => {
      // The preview iframe runs generated app content — accept MCP results
      // only from that iframe's window (and origin, when pinned). Without
      // this, any window able to post to Studio could spoof command
      // results once it observed a correlation id.
      if (!this.iframeRef || event.source !== this.iframeRef.contentWindow) return
      if (this.pinnedOrigin !== '*' && event.origin !== this.pinnedOrigin) return
      const data = event.data
      if (!data || data.type !== IFRAME_EVENTS.MCP_RESULT) return
      const payload = data.payload as
        | { id?: string; success?: boolean; data?: unknown; error?: string }
        | undefined
      if (!payload?.id) return
      const entry = this.pending.get(payload.id)
      if (!entry) return
      clearTimeout(entry.timer)
      this.pending.delete(payload.id)
      entry.resolve({
        success: payload.success ?? false,
        data: payload.data,
        error: payload.error,
      })
    }
    window.addEventListener('message', this.messageHandler)
  }

  /** Disconnect from the iframe. */
  disconnect(): void {
    if (this.messageHandler) {
      window.removeEventListener('message', this.messageHandler)
      this.messageHandler = null
    }
    // Reject all pending
    for (const [, entry] of this.pending) {
      clearTimeout(entry.timer)
      entry.resolve({ success: false, error: 'RuntimeBridge disconnected' })
    }
    this.pending.clear()
    this.iframeRef = null
  }

  /** Execute a cogo.runtime.* command via postMessage. */
  async execute(cmd: McpCommand): Promise<McpResult> {
    if (!cmd.tool.startsWith(RUNTIME_NS)) {
      return { success: false, error: `Invalid namespace: expected "${RUNTIME_NS}*"` }
    }
    if (!this.iframeRef?.contentWindow) {
      return { success: false, error: 'Preview iframe not connected' }
    }

    const id = crypto.randomUUID()
    const message = createIframeCommand(IFRAME_COMMANDS.MCP_EXECUTE, {
      id,
      tool: cmd.tool,
      params: cmd.params,
    })

    return new Promise<McpResult>((resolve) => {
      const timer = setTimeout(() => {
        this.pending.delete(id)
        resolve({ success: false, error: `Runtime command timeout (${TIMEOUT_MS}ms)` })
      }, TIMEOUT_MS)

      this.pending.set(id, { resolve, timer })
      // Pin the command (which carries the correlation id) to the iframe's
      // origin — broadcasting with '*' let whatever document currently
      // occupied the iframe observe ids and forge replies.
      this.iframeRef!.contentWindow!.postMessage(message, this.pinnedOrigin)
    })
  }

  get connected(): boolean {
    return this.iframeRef !== null
  }
}

/** Singleton host-side bridge for communicating with the Preview iframe. */
export const runtimeBridge = new RuntimeBridge()
